import { z } from 'zod';
import { createModel, partySchema } from '../../common';
import { dateSchema, idSchema } from '../../validation';
import { appHealthSchema, appIdSchema } from './app.model';

/**
 * When Hero should issue the government waybill for an order.
 *
 * - `on_first_pickup`   — default. Fires when the driver finishes the first
 *                         pickup destination, which is the moment the load
 *                         legally exists on the road and matches what a
 *                         dispatcher would do by hand.
 * - `on_driver_assign`  — earlier: as soon as a driver + vehicle are on the
 *                         order. Suits Orgs that want the document in the
 *                         driver's hand before they set off, at the cost of
 *                         more updates if the assignment changes.
 * - `manual_only`       — nothing automatic; dashboard button or WhatsApp.
 */
export const waybillTriggerSchema = z.enum([
  'on_first_pickup',
  'on_driver_assign',
  'manual_only',
]);
export type WaybillTrigger = z.infer<typeof waybillTriggerSchema>;

/**
 * Per-Org behaviour knobs for the Logisti / Bayan connector.
 *
 * Kept on the install rather than on `Org.settings` so that uninstalling and
 * reinstalling is a clean slate, and so each app's settings block stays typed
 * under its own key instead of widening the Org document.
 */
export const logistiSettingsSchema = z.object({
  /** When to issue the government waybill. */
  trigger: waybillTriggerSchema.default('on_first_pickup'),
  /** Close the Bayan waybill automatically when the order is delivered. */
  autoCloseOnDelivery: z.boolean().default(true),
  /** Cancel the Bayan waybill automatically when the order is cancelled. */
  autoCancelOnOrderCancel: z.boolean().default(true),
  /**
   * Bayan good-type id used when Hero's coarse `ItemType` can't pick a leaf
   * node (construction materials, chemicals, `other`). Without this, those
   * orders block on a validation error asking the user to choose.
   */
  defaultGoodTypeId: z.number().int().positive().optional(),
  /** Bayan payment-method id. Defaults to 1 (Cash) at mapping time. */
  defaultPaymentMethodId: z.number().int().positive().optional(),
  /**
   * Opt-in for `deliverToClient` waybills — goods handed to the recipient
   * against payment, which makes `fare` and per-item `price` mandatory in
   * Bayan's eyes.
   *
   * Enabling it does NOT flag every waybill. The mapper ANDs this with
   * `order.client`, so only orders that actually have a buyer file that way
   * and an internal repositioning move from the same org does not. See
   * `resolveDeliverToClient` in `bayan.mapper.ts`.
   */
  deliverToClient: z.boolean().default(false),
  /** `tradable` — whether only the named recipient may receive the shipment. */
  tradable: z.boolean().default(false),
  /** `paidBySender` — false means the recipient pays. */
  paidBySender: z.boolean().default(true),
});
export type LogistiSettings = z.infer<typeof logistiSettingsSchema>;

/**
 * The Org-supplied half of an app's auth pair.
 *
 * ## Why this is inline rather than its own collection
 *
 * Credentials used to live in a separate `IntegrationCredential` collection so
 * a list endpoint could not leak them. That protection actually comes from the
 * API layer: `appInstalledViewSchema` is an explicit allowlist that only ever
 * emits `accountIdMasked`, so the raw document never reaches a response. With
 * the leak closed at the boundary, a second collection bought nothing but an
 * extra round-trip on the hot path — every waybill issuance had to read it.
 *
 * ## Why `accountId` is plaintext
 *
 * For Logisti it holds Bayan's `client_id`, an account identifier the Org
 * reads off its own Bayan portal profile page. It is not a bearer secret: on
 * its own it grants nothing, because every call also needs Hero's platform
 * `app_id` + `app_key`, which live in GCP Secret Manager and never touch the
 * database.
 */
export const appCredentialsSchema = z.object({
  /**
   * The identifier the external app knows this Org by — Bayan's `client_id`,
   * a bank's beneficiary id, an ERP tenant key. Identifier, not a secret.
   */
  accountId: z.string().trim().min(1).optional(),

  /**
   * Reserved for genuinely secret material (OAuth refresh tokens, API keys).
   *
   * MUST be encrypted at rest before anything is written here. No app uses it
   * yet, and it is deliberately unimplemented rather than half-implemented:
   * the backend has no envelope-encryption service today. Adding one is the
   * prerequisite for any app that needs real secrets.
   */
  secretToken: z.string().optional(),

  /** When these credentials last authenticated successfully. */
  lastVerifiedAt: dateSchema.optional(),
});
export type AppCredentials = z.infer<typeof appCredentialsSchema>;

/**
 * How this install files with the provider.
 *
 * One account has exactly one row, and this is the setting on it — never a
 * second row. A tenant on `platform` who later gets their own ELM
 * `client_id` flips to `own_account` on the same document, which is what
 * keeps "am I connected" a single question with a single answer.
 *
 * - `own_account` — the tenant's own licence, `credentials.accountId` set.
 * - `platform`    — Hero's licence files it, and the document declares the
 *                   tenant's CR (`Org.legal.registrationId`) as the carrier.
 * - `individual`  — Hero's licence files it for an owner-operator, who Bayan
 *                   declares as an `INDIVIDUAL` carrier. Exempt from the CR,
 *                   but `Driver.legal.id` and `Driver.legal.idIssueNumber`
 *                   are what the document names, so they stand in for it.
 *
 * The identity is the point. Each mode names a different field that has to
 * exist before a وثيقة can be filed at all, and an install without one looks
 * healthy right up until the first load fails at the mapper.
 */
export const appFilingModeSchema = z.enum(['own_account', 'platform', 'individual']);
export type AppFilingMode = z.infer<typeof appFilingModeSchema>;

/**
 * `AppInstalled db model` — one account's installation of one app.
 *
 * ## Tenancy
 *
 * Hero holds the platform-level credentials for each app (env / GCP Secret
 * Manager); each tenant holds its own account identifier and its own license
 * with the provider. One row per `(appId, owner)` pair — enforced by a unique
 * index, since the same owner installing twice would get two chances to act,
 * and since {@link appFilingModeSchema} only means anything if there is
 * exactly one row to carry it.
 *
 * ## Why `owner` and not `org`
 *
 * An owner-operator has no Org anywhere — not on their account and not on
 * their orders — yet Bayan files for them happily as an `INDIVIDUAL`
 * carrier. While this field was `org`, that person could not hold an install
 * at all, so `/apps/installed` threw for them and the dashboard had nothing
 * to render. `Order.waybillBayan.issuingOrg` already stores a driver party
 * for exactly this case, so the shape has precedent.
 *
 * ## Naming
 *
 * NOT `Connection`. That collection already exists and models a bilateral
 * relationship between two network parties (relations-v2). This one is a
 * tenant-to-vendor install and shares nothing with it beyond the English
 * word.
 *
 * @see docs/apps/bayan.md
 */
export const AppInstalledSchema = createModel({
  /** Which app this install is for. Unique per owner. */
  appId: appIdSchema,

  /**
   * Who owns this install. `target: 'org'` for a company, `target: 'driver'`
   * for an owner-operator with no Org. See the note above on the rename.
   */
  owner: partySchema,

  /** Which licence files, and therefore which identity has to be on file. */
  filingMode: appFilingModeSchema.default('own_account'),

  /**
   * Whether the install is active.
   *
   * Uninstalling sets this to `false` and clears the credentials rather than
   * deleting the row, so the audit trail (`AppLog` rows, issued waybills)
   * keeps pointing at something.
   *
   * `false` is a hard stop on issuance, including Hero's own licence. That
   * is worth stating because it did not used to be: platform filing was an
   * unconditional fallback, so disconnecting cleared the credentials and
   * documents kept being filed. A row that is absent is a different case —
   * it means "never materialized", and the issuer creates one when the
   * identity is there.
   */
  enabled: z.boolean().default(true),

  /** Last known health. See {@link appHealthSchema}. */
  health: appHealthSchema.default('unknown'),

  /**
   * The tenant's own credentials for this app. Cleared on uninstall.
   *
   * Absent on a `platform` / `individual` row by definition — those file on
   * Hero's account, and only `credentials.accountId` counts as an own
   * account anywhere in the connector.
   */
  credentials: appCredentialsSchema.optional(),

  /** Logisti behaviour knobs. Present on `logisti` installs. */
  logisti: logistiSettingsSchema.optional(),

  /** When the credentials were last verified against the provider. */
  verifiedAt: dateSchema.optional(),

  /** Human-readable reason the install is degraded / unauthorized. */
  lastError: z.string().optional(),

  /** When `lastError` was recorded. */
  lastErrorAt: dateSchema.optional(),

  /** Timestamp of the most recent successful provider call. */
  lastSyncAt: dateSchema.optional(),

  /** `User` `_id` hex string of whoever installed it — for the settings UI. */
  installedBy: idSchema.optional(),
});

export type AppInstalled = z.infer<typeof AppInstalledSchema.model>;
export type AppInstalledData = z.infer<typeof AppInstalledSchema.data>;
export type AppInstalledPartial = z.infer<typeof AppInstalledSchema.partial>;
